import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Subscription } from '../models/subscription';
import { useUpdateSubscriptionMutation, useGetSubscriptionByIdQuery } from '../../app/services/subscription';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Chip,
  Stack,
} from '@mui/material';
interface EditSubscriptionModalProps {
  isOpen: boolean;
  subscription: Subscription | null;
  onClose: () => void;
  onSaved: (updated: Subscription) => void;
  refreshSubscriptions: () => void; 
  productName?: string;
}

const EditSubscriptionModal: React.FC<EditSubscriptionModalProps> = ({
  isOpen,
  subscription,
  onClose,
  onSaved,
  refreshSubscriptions,
  productName
}) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [description, setDescription] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  
  const [updateSubscription] = useUpdateSubscriptionMutation();

  useEffect(() => {
    if (isOpen && subscription) {
      setName(subscription.name || '');
      setDescription(subscription.description || '');
      setError(null);
    }
  }, [isOpen, subscription]);

  const { data: subDetails, refetch: refetchSubDetails } = useGetSubscriptionByIdQuery(
    subscription ? { subscriptionId: subscription.subscriptionId, productId: subscription.productId } : ({} as any),
    {
      skip: !subscription || !isOpen,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (subDetails && isOpen) {
      setDescription(subDetails.description || '');
    }
  }, [subDetails, isOpen]);

  useEffect(() => {
    if (isOpen && subscription) {
      refetchSubDetails();
    }
  }, [isOpen, subscription, refetchSubDetails]);

  const canSave = useMemo(() => {
    const nameChanged = !!subscription && name.trim().length > 0 && name.trim() !== subscription.name;
    const descChanged = (subscription?.description || '') !== description;
    return !!subscription && (nameChanged || descChanged);
  }, [subscription, name, description]);


const handleSave = useCallback(async () => {
    if (!subscription) return;
    if (!name.trim()) {
      setError('Tier name is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const updated = await updateSubscription({
        subscriptionId: subscription.subscriptionId,
        name: name.trim(),
        description: description?.trim() || '',
      }).unwrap();
      toast.success('Subscription tier renamed successfully');
      onSaved({ ...subscription, name: updated.name, description: updated.description });
      onClose();
      refreshSubscriptions();
    } catch (err: any) {
      const message = err?.data?.title || err?.data || err?.message || 'Failed to update subscription tier';
      setError(typeof message === 'string' ? message : 'Failed to update subscription tier');
      toast.error(typeof message === 'string' ? message : 'Failed to update subscription tier');
    } finally {
      setSaving(false);
    }
  }, [subscription, name, description, onSaved, onClose, updateSubscription, refreshSubscriptions]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Enter') {
        if (!saving && canSave) {
          e.preventDefault();
          handleSave();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, canSave, saving, onClose, handleSave]);

  if (!subscription) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          Edit Subscription Tier
          {productName && (
            <Chip size="small" label={productName} color="primary" variant="outlined" sx={{ ml: 'auto' }} />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, p: 1, border: '1px solid', borderColor: 'error.light', borderRadius: 1, bgcolor: 'error.50' }}>
            <span>{error}</span>
            <Button size="small" onClick={() => setError(null)}>Dismiss</Button>
          </Stack>
        )}
        <Stack spacing={2}>
          <TextField
            label="Tier Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Gold, Silver, Bronze"
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description shown for this tier (max 500 chars)"
            inputProps={{ maxLength: 500 }}
            multiline
            minRows={3}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} disabled={!canSave || saving} variant="contained">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSubscriptionModal;
